import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useToast } from "@/hooks/use-toast";
import { QuoteProduct } from "@/types/quote";

type QuoteListContextType = {
  items: QuoteProduct[];
  addToQuote: (product: {
    id: string;
    name: string;
    slug: string;
    category_id?: string | null;
    category_name?: string;
    specifications?: Record<string, string>;
  }, quantity?: number) => void;
  removeFromQuote: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearQuote: () => void;
  itemCount: number;
};

const QuoteListContext = createContext<QuoteListContextType | null>(null);

export const QuoteListProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<QuoteProduct[]>([]);
  const { toast } = useToast();

  const itemCount = items.reduce((acc, item) => acc + item.quantity, 0);

  const addToQuote = useCallback((
    product: {
      id: string;
      name: string;
      slug: string;
      category_id?: string | null;
      category_name?: string;
      specifications?: Record<string, string>;
    },
    quantity: number = 1
  ) => {
    setItems(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      
      if (existing) {
        toast({
          title: "Cantidad actualizada",
          description: `${product.name} ahora tiene ${existing.quantity + quantity} unidades`,
        });
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      
      toast({
        title: "Producto agregado",
        description: `${product.name} agregado a tu cotización`,
      });
      
      return [...prev, {
        product_id: product.id,
        product_name: product.name,
        product_slug: product.slug,
        category_id: product.category_id || undefined,
        category_name: product.category_name,
        quantity,
        specifications: product.specifications,
      }];
    });
  }, [toast]);

  const removeFromQuote = useCallback((productId: string) => {
    setItems(prev => {
      const item = prev.find(i => i.product_id === productId);
      if (item) {
        toast({
          title: "Producto eliminado",
          description: `${item.product_name} eliminado de tu cotización`,
        });
      }
      return prev.filter(item => item.product_id !== productId);
    });
  }, [toast]);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity < 1) {
      removeFromQuote(productId);
      return;
    }
    
    setItems(prev =>
      prev.map(item =>
        item.product_id === productId ? { ...item, quantity } : item
      )
    );
  }, [removeFromQuote]);

  const clearQuote = useCallback(() => {
    setItems([]);
    toast({
      title: "Cotización limpiada",
      description: "Todos los productos han sido eliminados",
    });
  }, [toast]);

  return (
    <QuoteListContext.Provider
      value={{
        items,
        addToQuote,
        removeFromQuote,
        updateQuantity,
        clearQuote,
        itemCount,
      }}
    >
      {children}
    </QuoteListContext.Provider>
  );
};

export const useQuoteList = () => {
  const context = useContext(QuoteListContext);
  if (!context) {
    throw new Error("useQuoteList must be used within a QuoteListProvider");
  }
  return context;
};
